import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import { MessageEditor } from "../../components/editor/MessageEditor";
import { Button } from "../../components/ui/Button";
import { cn } from "../../components/ui/cn";
import { SearchIcon } from "../../components/ui/icons";
import { ApiError } from "../../lib/api";
import {
  checklistInsert,
  checklistOf,
  toggleItem,
  UNCHECKED,
  type ChecklistItem,
} from "../../lib/checklist";
import { crmApi } from "../../lib/crm-api";
import { clearCopy, newerOf, readCopy, writeCopy } from "../../lib/device-copy";
import {
  dayTitle,
  fieldApi,
  fieldKeys,
  ukToday,
  type QuickNote,
  type QuickNoteRow,
  type QuickNoteTask,
} from "../../lib/field-api";
import { queryKeys } from "../../lib/queryKeys";
import { relativeTime } from "../../lib/time";
import type { User } from "../../types";
import { FieldTabs } from "./FieldShell";

/**
 * Quick Notes: a fresh page every day, like the iPhone's Notes app.
 *
 * The list down the side holds every day something was written, newest first;
 * today sits at the top whether or not it has anything on it yet, and opens by
 * default. On a phone the list and the page are two screens, as they are on
 * the iPhone -- `/field/notes` is the list and `/field/notes/<day>` the page.
 *
 * Nothing is stored until something is typed, and a page emptied again is
 * deleted on the server, so a day with nothing written leaves nothing behind.
 * Saving is the meeting notes' machinery: a couple of seconds after typing
 * stops, a copy on the device in the meantime, and quiet retries when there
 * is no signal. See `NotesEditor`.
 *
 * Private to the rep. The one way out is an @company mention, which files the
 * lines mentioning that company on its timeline -- the editor says so under
 * the page when there is one.
 */

const SAVE_AFTER_MS = 1_500;
const RETRY_MS = 10_000;

export function QuickNotes({ user }: { user: User }) {
  const params = useParams();
  const today = useUkToday();
  const wide = useWide();
  /* `/field/notes` opens today's page beside the list on a wide screen, and
     is the list on its own on a phone. */
  const day = params.day ?? (wide ? today : null);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* On a phone, an open page is a screen of its own with a back arrow
          instead of the tabs, as Notes does it. */}
      {wide || !day ? <FieldTabs user={user} tab="notes" /> : null}

      <div className="flex min-h-0 flex-1">
        {wide || !day ? (
          <NotesList today={today} selected={day} wide={wide} />
        ) : null}
        {day ? (
          <NotePage key={day} day={day} today={today} user={user} wide={wide} />
        ) : null}
      </div>
    </div>
  );
}

// ── The list ────────────────────────────────────────────────────────────────

const GROUP_LABELS: Record<string, string> = {
  today: "Today",
  week: "Previous 7 days",
  month: "Previous 30 days",
};

function groupLabel(group: string): string {
  if (GROUP_LABELS[group]) return GROUP_LABELS[group];
  const [y, m] = group.split("-").map(Number) as [number, number];
  const date = new Date(Date.UTC(y, m - 1, 15));
  const sameYear = y === new Date().getFullYear();
  return date.toLocaleDateString("en-GB", {
    month: "long",
    ...(sameYear ? {} : { year: "numeric" }),
    timeZone: "UTC",
  });
}

function NotesList({
  today,
  selected,
  wide,
}: {
  today: string;
  selected: string | null;
  wide: boolean;
}) {
  const [search, setSearch] = useState("");
  const q = useDebounced(search.trim(), 250);

  const list = useQuery({
    queryKey: fieldKeys.notes(q),
    queryFn: () => fieldApi.notes(q),
    placeholderData: (previous) => previous,
  });

  const rows = list.data?.notes ?? [];
  const searching = q.length > 0;

  /* Today is always first, written on or not -- it is where a new thought
     goes. Pinned here rather than returned by the server, which stores
     nothing for a blank page. */
  const todayRow = rows.find((row) => row.day === today) ?? null;
  const older = rows.filter((row) => row.day !== today);
  const groups: [string, QuickNoteRow[]][] = [];
  for (const row of older) {
    const last = groups[groups.length - 1];
    if (last && last[0] === row.group) last[1].push(row);
    else groups.push([row.group, [row]]);
  }

  return (
    <aside
      className={cn(
        "flex min-h-0 flex-col bg-surface",
        wide ? "w-72 shrink-0 border-r border-line xl:w-80" : "w-full",
      )}
    >
      <div className="border-b border-line p-2">
        <label className="flex h-8 items-center gap-2 rounded-md border border-line bg-surface-2 px-2 text-sm">
          <SearchIcon className="size-3.5 shrink-0 text-faint" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search all notes"
            aria-label="Search all notes"
            className="min-w-0 flex-1 bg-transparent text-content outline-none placeholder:text-faint"
          />
        </label>
      </div>

      <nav aria-label="Quick notes" className="min-h-0 flex-1 overflow-y-auto">
        {searching ? (
          rows.length === 0 ? (
            <p className="p-4 text-sm text-faint">
              {list.isFetching ? "Searching…" : `Nothing mentions “${q}”.`}
            </p>
          ) : (
            <ul>
              {rows.map((row) => (
                <NoteRow key={row.day} row={row} today={today} active={row.day === selected} query={q} />
              ))}
            </ul>
          )
        ) : (
          <>
            <GroupHeading>Today</GroupHeading>
            <ul>
              <NoteRow
                row={
                  todayRow ?? {
                    day: today,
                    preview: "",
                    match: null,
                    updatedAt: "",
                    edited: false,
                    group: "today",
                  }
                }
                today={today}
                active={selected === today}
              />
            </ul>
            {groups.map(([group, entries]) => (
              <section key={group}>
                <GroupHeading>{groupLabel(group)}</GroupHeading>
                <ul>
                  {entries.map((row) => (
                    <NoteRow key={row.day} row={row} today={today} active={row.day === selected} />
                  ))}
                </ul>
              </section>
            ))}
            {list.isSuccess && older.length === 0 ? (
              <p className="px-4 py-3 text-xs text-faint">
                Earlier days appear here once you've written on them.
              </p>
            ) : null}
          </>
        )}
      </nav>
    </aside>
  );
}

function GroupHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="sticky top-0 z-[1] bg-surface px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-faint">
      {children}
    </h2>
  );
}

function NoteRow({
  row,
  today,
  active,
  query,
}: {
  row: QuickNoteRow;
  today: string;
  active: boolean;
  query?: string;
}) {
  const isToday = row.day === today;
  return (
    <li>
      <Link
        to={`/field/notes/${row.day}`}
        aria-current={active ? "page" : undefined}
        className={cn(
          "mx-2 block rounded-lg px-2 py-2",
          active ? "bg-brand-50 dark:bg-brand-500/15" : "hover:bg-surface-2",
        )}
      >
        <span className="flex items-baseline gap-2">
          <span className="text-sm font-medium text-content">{dayTitle(row.day)}</span>
          {row.edited ? <span className="text-[10px] text-faint">edited</span> : null}
        </span>
        <span className="block truncate text-xs text-muted">
          {query && row.match ? (
            <Highlight text={row.match} query={query} />
          ) : row.preview ? (
            row.preview
          ) : isToday ? (
            <span className="text-faint">New note</span>
          ) : (
            <span className="text-faint">No text</span>
          )}
        </span>
      </Link>
    </li>
  );
}

function Highlight({ text, query }: { text: string; query: string }) {
  const at = text.toLowerCase().indexOf(query.toLowerCase());
  if (at === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <mark className="rounded bg-amber-100 px-0.5 text-content dark:bg-amber-500/25">
        {text.slice(at, at + query.length)}
      </mark>
      {text.slice(at + query.length)}
    </>
  );
}

// ── One page ────────────────────────────────────────────────────────────────

function NotePage({
  day,
  today,
  user,
  wide,
}: {
  day: string;
  today: string;
  user: User;
  wide: boolean;
}) {
  const note = useQuery({
    queryKey: fieldKeys.note(day),
    queryFn: () => fieldApi.note(day),
    /* Read once per opening. Refetching under somebody typing would hand
       the editor a document to fight the caret with. */
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  return (
    <section className="flex min-h-0 min-w-0 flex-1 flex-col bg-surface">
      <header className="flex items-center gap-2 border-b border-line px-3 py-2 sm:px-5">
        {!wide ? (
          <Link
            to="/field/notes"
            className="-ml-1 rounded px-1 text-sm text-brand-600 dark:text-brand-400"
          >
            ‹ Notes
          </Link>
        ) : null}
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-base font-semibold text-content">
            {day === today ? `Today · ${dayTitle(day)}` : dayTitle(day, true)}
          </h1>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 sm:px-5">
        {note.isLoading ? (
          <p className="text-sm text-faint">Opening…</p>
        ) : note.isError ? (
          <p className="text-sm text-muted">
            {note.error instanceof ApiError ? note.error.message : "Could not open this page."}
          </p>
        ) : note.data ? (
          <NoteEditor note={note.data} day={day} today={today} user={user} />
        ) : null}
      </div>
    </section>
  );
}

function NoteEditor({
  note,
  day,
  today,
  user,
}: {
  note: QuickNote;
  day: string;
  today: string;
  user: User;
}) {
  const client = useQueryClient();
  const copyKey = `pulse.quick-note.${user.id}.${day}`;

  const [start] = useState(() =>
    newerOf(
      { value: note.html, at: note.updatedAt },
      readCopy<string>(copyKey),
      (a, b) => a === b,
    ),
  );
  const restored = start?.fromDevice === true;
  const [html, setHtml] = useState(start?.value ?? "");
  const [editorKey, setEditorKey] = useState(0);
  const saved = useRef(note.html);
  const latest = useRef(html);
  latest.current = html;
  const [status, setStatus] = useState<"saved" | "dirty" | "saving" | "failed">(
    restored ? "dirty" : "saved",
  );
  const [meta, setMeta] = useState({ at: note.updatedAt, edited: note.edited });
  const [tasks, setTasks] = useState<QuickNoteTask[]>(note.tasks);

  const refreshAround = useCallback(() => {
    void client.invalidateQueries({ queryKey: fieldKeys.notesRoot });
    void client.invalidateQueries({ queryKey: fieldKeys.home });
  }, [client]);

  const save = useMutation({
    mutationFn: (body: string) => fieldApi.saveNote(day, body),
    onMutate: () => setStatus("saving"),
    onSuccess: (result, body) => {
      saved.current = body;
      if (latest.current === body) clearCopy(copyKey);
      setMeta({ at: result.updatedAt, edited: result.edited });
      setStatus((current) => (current === "saving" ? "saved" : current));
      client.setQueryData<QuickNote>(fieldKeys.note(day), (current) =>
        current ? { ...current, html: body, updatedAt: result.updatedAt, edited: result.edited } : current,
      );
      refreshAround();
    },
    onError: () => setStatus("failed"),
  });

  const { mutate } = save;
  const flush = useCallback(
    (body: string) => {
      if (body === saved.current) return;
      mutate(body);
    },
    [mutate],
  );

  useEffect(() => {
    if (html === saved.current) return;
    writeCopy(copyKey, html);
    setStatus((current) => (current === "saving" ? current : "dirty"));
    const timer = window.setTimeout(() => flush(html), SAVE_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [html, flush, copyKey]);

  useEffect(() => {
    if (status !== "failed") return;
    const timer = window.setTimeout(() => mutate(latest.current), RETRY_MS);
    return () => window.clearTimeout(timer);
  }, [status, mutate]);

  /* Leaving the page -- another day, another tab -- saves what is left. */
  useEffect(
    () => () => {
      if (latest.current !== saved.current) {
        const body = latest.current;
        void fieldApi
          .saveNote(day, body)
          .then((result) => {
            clearCopy(copyKey);
            client.setQueryData<QuickNote>(fieldKeys.note(day), (current) =>
              current ? { ...current, html: body, updatedAt: result.updatedAt, edited: result.edited } : current,
            );
            void client.invalidateQueries({ queryKey: fieldKeys.notesRoot });
            void client.invalidateQueries({ queryKey: fieldKeys.home });
          })
          .catch(() => undefined);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [day],
  );

  useEffect(() => {
    if (status === "saved") return;
    const onUnload = (event: BeforeUnloadEvent): void => event.preventDefault();
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [status]);

  const items = checklistOf(html);
  const mentions = mentionedCompanies(html);

  return (
    <div className="mx-auto max-w-3xl">
      <MessageEditor
        key={`${day}-${editorKey}`}
        initialHtml={html}
        onChange={setHtml}
        uploads={[]}
        onUploadsChange={() => undefined}
        onError={() => undefined}
        allowAttachments={false}
        companyMentions
        placeholder={
          day === today
            ? "Anything from the road — a part number, who to ring back. Type @ to mention a company."
            : "Nothing written on this day."
        }
        surfaceHeight="min-h-[50vh]"
        extraTools={(tools) => (
          <button
            type="button"
            title="Checklist item — tick it off, or make it a task"
            aria-label="Checklist item"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => tools.insertHtml(checklistInsert())}
            className="flex h-7 items-center gap-1 rounded px-1.5 text-xs text-muted hover:bg-surface hover:text-brand-600 dark:hover:text-brand-400"
          >
            <span aria-hidden>{UNCHECKED}</span> Checklist
          </button>
        )}
      />

      <p className="mt-1 flex flex-wrap items-center gap-x-2 text-[11px] text-faint">
        <span className={cn(status === "failed" && "font-medium text-red-600 dark:text-red-400")}>
          {status === "saving"
            ? "Saving…"
            : status === "dirty"
              ? "Editing…"
              : status === "failed"
                ? "No connection — kept on this device, saving when you're back"
                : meta.at
                  ? `Saved ${relativeTime(meta.at)}${meta.edited ? " · edited after the day" : ""}`
                  : "Saves as you type · only you can see this"}
        </span>
        {restored && status !== "saved" ? (
          <span className="text-amber-700 dark:text-amber-400">Put back what you wrote offline.</span>
        ) : null}
        {status === "failed" ? (
          <button
            type="button"
            onClick={() => save.mutate(html)}
            className="text-brand-600 hover:underline dark:text-brand-400"
          >
            Try again
          </button>
        ) : null}
        {mentions.length > 0 ? (
          <span className="ml-auto">
            Lines mentioning {joinNames(mentions.map((company) => company.name))} go on{" "}
            {mentions.length === 1 ? "its" : "their"} timeline
          </span>
        ) : null}
      </p>

      {items.length > 0 ? (
        <Actions
          day={day}
          items={items}
          tasks={tasks}
          saving={html !== saved.current}
          onToggle={(index) => {
            setHtml(toggleItem(html, index));
            setEditorKey((key) => key + 1);
          }}
          onRaised={(next) => {
            setTasks(next);
            client.setQueryData<QuickNote>(fieldKeys.note(day), (current) =>
              current ? { ...current, tasks: next } : current,
            );
            void client.invalidateQueries({ queryKey: fieldKeys.home });
            void client.invalidateQueries({ queryKey: fieldKeys.tasks });
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * The checklist under the page: tick things off, or make one a Lane task.
 *
 * A task is the ordinary CRM task, so it needs a company -- the one the line
 * @mentions when it mentions one, or one picked here. It then shows on Home's
 * Pending tasks card with everything else.
 */
function Actions({
  day,
  items,
  tasks,
  saving,
  onToggle,
  onRaised,
}: {
  day: string;
  items: ChecklistItem[];
  tasks: QuickNoteTask[];
  saving: boolean;
  onToggle: (index: number) => void;
  onRaised: (tasks: QuickNoteTask[]) => void;
}) {
  const [making, setMaking] = useState<number | null>(null);

  return (
    <div className="mt-4 rounded-lg border border-line">
      <p className="border-b border-line px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-faint">
        Checklist
      </p>
      <ul>
        {items.map((item) => {
          const task = tasks.find((entry) => stripBox(entry.text) === item.text);
          return (
            <li key={item.index} className="border-b border-line px-3 py-1.5 last:border-b-0">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={item.done}
                  onChange={() => onToggle(item.index)}
                  aria-label={item.done ? "Mark as not done" : "Mark as done"}
                  className="size-4 shrink-0 accent-brand-600"
                />
                <span className={cn("min-w-0 flex-1 text-sm text-content", item.done && "text-faint line-through")}>
                  {item.text || <em className="text-faint">Empty item</em>}
                </span>
                {task ? (
                  <a
                    href={task.url}
                    target="_blank"
                    rel="noreferrer"
                    className="shrink-0 text-[11px] text-brand-600 hover:underline dark:text-brand-400"
                  >
                    Task{task.assigneeNames[0] ? ` · ${task.assigneeNames[0].split(" ")[0]}` : ""} ↗
                  </a>
                ) : item.text ? (
                  <button
                    type="button"
                    onClick={() => setMaking(making === item.index ? null : item.index)}
                    className="shrink-0 rounded-md border border-line px-2 py-0.5 text-[11px] text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400"
                  >
                    Make task
                  </button>
                ) : null}
              </div>
              {making === item.index ? (
                <TaskForm
                  day={day}
                  item={item}
                  saving={saving}
                  onDone={(next) => {
                    setMaking(null);
                    onRaised(next);
                  }}
                />
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function TaskForm({
  day,
  item,
  saving,
  onDone,
}: {
  day: string;
  item: ChecklistItem;
  saving: boolean;
  onDone: (tasks: QuickNoteTask[]) => void;
}) {
  const [company, setCompany] = useState(item.company);
  const [picked, setPicked] = useState<string | null>(null);
  const [due, setDue] = useState("");

  const boards = useQuery({
    queryKey: queryKeys.crmLaneBoards(company?.id),
    queryFn: () => crmApi.laneBoards(company?.id),
    enabled: company !== null,
    staleTime: 5 * 60_000,
  });
  const self = boards.data?.selfAssigneeId ?? "";
  const owner = picked ?? self;

  const create = useMutation({
    mutationFn: () =>
      fieldApi.raiseTask(day, {
        text: item.text,
        companyId: company!.id,
        assigneeIds: owner ? [owner] : undefined,
        /* The end of the working day it is due, so it is not overdue at nine. */
        dueAt: due ? new Date(`${due}T17:00:00`).toISOString() : null,
      }),
    onSuccess: (result) => onDone(result.tasks),
  });

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 pl-6 text-xs">
      {company ? (
        <span className="flex items-center gap-1 text-muted">
          For
          <span className="font-medium text-content">{company.name}</span>
          {!item.company || item.company.id !== company.id ? (
            <button
              type="button"
              onClick={() => setCompany(null)}
              className="text-faint hover:text-content"
              aria-label="Choose another company"
            >
              ×
            </button>
          ) : null}
        </span>
      ) : (
        <CompanyPicker onPick={setCompany} />
      )}

      {company ? (
        <>
          <label className="flex items-center gap-1 text-muted">
            Owner
            <select
              value={owner}
              onChange={(event) => setPicked(event.target.value)}
              className="h-7 rounded-md border border-line bg-surface px-1.5 text-xs text-content"
            >
              {self ? <option value={self}>You</option> : <option value="">Account manager</option>}
              {(boards.data?.assignees ?? [])
                .filter((person) => person.id !== self)
                .map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name ?? person.email}
                  </option>
                ))}
            </select>
          </label>
          <label className="flex items-center gap-1 text-muted">
            Due
            <input
              type="date"
              value={due}
              onChange={(event) => setDue(event.target.value)}
              className="h-7 rounded-md border border-line bg-surface px-1.5 text-xs text-content"
            />
          </label>
          <Button
            size="sm"
            /* The line has to be on the server first -- the task is raised
               against the saved page. */
            disabled={create.isPending || saving}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "Creating…" : saving ? "Saving…" : "Create task"}
          </Button>
        </>
      ) : null}
      {create.isError ? (
        <span className="text-red-600">
          {create.error instanceof ApiError ? create.error.message : "Could not create that task."}
        </span>
      ) : null}
    </div>
  );
}

/** A CRM company search for a task whose line mentions none. */
function CompanyPicker({ onPick }: { onPick: (company: { id: number; name: string }) => void }) {
  const [text, setText] = useState("");
  const q = useDebounced(text.trim(), 250);
  const hits = useQuery({
    queryKey: ["crm", "mention-search", q],
    queryFn: () => crmApi.search(q),
    enabled: q.length > 0,
    staleTime: 60_000,
  });

  return (
    <div className="relative">
      <input
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder="Which company is it for?"
        aria-label="Company for this task"
        className="h-7 w-56 rounded-md border border-line bg-surface px-2 text-xs text-content"
        autoFocus
      />
      {q && hits.data?.results.length ? (
        <ul className="absolute left-0 top-8 z-20 w-64 overflow-hidden rounded-lg border border-line bg-surface p-1 shadow-lg">
          {hits.data.results.slice(0, 6).map((company) => (
            <li key={company.id}>
              <button
                type="button"
                onClick={() => onPick({ id: company.id, name: company.name })}
                className="block w-full truncate rounded px-2 py-1 text-left text-xs text-content hover:bg-surface-2"
              >
                {company.name}
                {company.city ? <span className="text-faint"> · {company.city}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function stripBox(text: string): string {
  return text.replace(/^[\s☐☑]+/u, "").trim();
}

function mentionedCompanies(html: string): { id: number; name: string }[] {
  if (!html.includes("data-company")) return [];
  const doc = new DOMParser().parseFromString(html, "text/html");
  const seen = new Map<number, string>();
  doc.querySelectorAll<HTMLElement>("[data-company]").forEach((node) => {
    const id = Number(node.dataset.company);
    if (Number.isInteger(id) && id > 0 && !seen.has(id)) {
      seen.set(id, (node.textContent ?? "").replace(/^@/, "").trim());
    }
  });
  return [...seen].map(([id, name]) => ({ id, name }));
}

function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/**
 * Today's UK date, rechecked every minute so the notepad turns over at
 * midnight while it is open -- a rep who leaves it up overnight comes back to
 * a fresh page rather than yesterday's.
 */
function useUkToday(): string {
  const [today, setToday] = useState(ukToday);
  useEffect(() => {
    const timer = window.setInterval(() => setToday(ukToday()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  return today;
}

/** Whether there is room for the list and the page side by side. */
function useWide(): boolean {
  const query = "(min-width: 768px)";
  const [wide, setWide] = useState(() => window.matchMedia?.(query).matches ?? true);
  useEffect(() => {
    const media = window.matchMedia?.(query);
    if (!media) return;
    const onChange = () => setWide(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);
  return wide;
}

function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

